/**
 * Dev-session surface — the ONE derivation of which account a local dev session mints
 * (working-conventions §17: `/internal/*` gets the internal admin, company surfaces get
 * the company admin).
 *
 * ── Why this module exists (VRG-564) ──────────────────────────────────────────────
 *
 * The rule was hand-rolled TWICE — once as `INITIAL_SURFACE` in `hooks/useWorkOSAuth.ts`
 * and once as `DEV_INITIAL_SURFACE` in `services/index.ts` — the second carrying the
 * comment "Must match useWorkOSAuth's DEV_DEFAULT_SURFACE". A rule maintained in two
 * places by hand is §36's trigger, and here it was worse than untidy: the three mint
 * sites race for one cookie, so two copies that disagree resolve as last-writer-wins.
 *
 * ── The defect it fixes ───────────────────────────────────────────────────────────
 *
 * `VITE_DEV_DEFAULT_SURFACE` was DOCUMENTED as
 *
 *     "Default surface for a load that isn't clearly a company/internal path
 *      (e.g. the root the preview opens on)"
 *
 * and IMPLEMENTED as "any path that is not `/internal/*`" — which is the entire company
 * surface. So a flag whose stated job is "open the preview's ROOT as the internal admin"
 * silently minted the internal admin on `/studio`, `/certificates` and every
 * `/settings/*` page as well.
 *
 * That is not hypothetical. The `:5307` preview was started from a shell command that
 * carried `VITE_DEV_DEFAULT_SURFACE=internal` (copied from an internal epic's template;
 * the launch.json entry never had it, which is why reading launch.json cleared the flag
 * of suspicion). Every company-facing surface verified against that server — a whole
 * Studio pass — ran with internal-admin permissions, and `/studio` showed "No company is
 * associated with your account yet" because `platform_admin` genuinely has no company.
 * A permission gap, a role-scoped empty state or a company-scoped 403 would all have
 * been invisible. Precisely the class of bug §17 exists to prevent.
 *
 * The var keeps its purpose and loses its blast radius: it now applies ONLY to a path
 * that carries no surface signal of its own (the root, the auth entry pages). A request
 * for a NAMED company surface resolves to the company account, whatever the default says.
 *
 * The derivation is pure and exported so `test/dev-surface.guard.spec.ts` can pin it.
 */

import axios from 'axios'

export type DevSurface = 'company' | 'internal'

export const INTERNAL_PATH_PREFIX = '/internal'

/** Strip a trailing slash so `/internal/` and `/internal` classify identically. */
const normalizePath = (pathname: string): string => {
  const p = String(pathname ?? '')
  if (p.length > 1 && p.endsWith('/')) return p.replace(/\/+$/, '') || '/'
  return p || '/'
}

/**
 * Segment-boundary match, not `startsWith`. `/internalise` is not an internal route, and
 * the same trap has already been paid for once in this repo (§42: `/tags` is not an
 * ancestor of `/tags-shop`).
 */
export const isInternalPath = (pathname: string): boolean => {
  const p = normalizePath(pathname)
  return p === INTERNAL_PATH_PREFIX || p.startsWith(`${INTERNAL_PATH_PREFIX}/`)
}

/**
 * Paths that genuinely carry no surface signal, and are therefore the ONLY ones the
 * `VITE_DEV_DEFAULT_SURFACE` override may speak for: the root the preview opens on, and
 * the pre-auth entry pages. Everything else names a surface and is taken at its word.
 */
const SURFACE_NEUTRAL_EXACT = new Set([
  '/',
  '/sign-in',
  '/sign-up',
  '/signup',
  '/auth/callback',
])
const SURFACE_NEUTRAL_PREFIXES = ['/sign-up/', '/signup/']

export const isSurfaceNeutralPath = (pathname: string): boolean => {
  const p = normalizePath(pathname)
  if (SURFACE_NEUTRAL_EXACT.has(p)) return true
  return SURFACE_NEUTRAL_PREFIXES.some((prefix) => p.startsWith(prefix))
}

/**
 * Takes `unknown` rather than `{ VITE_DEV_DEFAULT_SURFACE?: string }` deliberately. The only
 * production caller passes `import.meta.env`, whose `ImportMetaEnv` is an INTERFACE this app
 * does not augment with this key — and an interface with no index signature shares "no
 * properties in common" with a structural type naming a key it lacks, which TypeScript
 * rejects outright (TS2559). Widening here beats declaring the key in `vite-env.d.ts`: the
 * value is read in exactly one place, and it keeps the function callable with a plain object
 * from the guard spec.
 */
export const readDefaultDevSurface = (env?: unknown): DevSurface => {
  const value = (env as { VITE_DEV_DEFAULT_SURFACE?: unknown } | null | undefined)
    ?.VITE_DEV_DEFAULT_SURFACE
  return value === 'internal' ? 'internal' : 'company'
}

export type DevSurfaceInput = {
  pathname: string
  /** VERA-876 — see `describeDevSurface`. */
  veraSpaceActive?: boolean
  defaultSurface?: DevSurface
}

/**
 * The derivation, with the reason it landed where it did — the reason is what makes a
 * wrong account debuggable from the console instead of by process archaeology, which is
 * how VRG-564 actually had to be found.
 */
export const describeDevSurface = (
  input: DevSurfaceInput,
): { surface: DevSurface; reason: string } => {
  const defaultSurface = input.defaultSurface ?? 'company'

  // VERA-876: a Vera staffer inside a company's SPACE is fundamentally internal — they
  // entered from /internal holding an internal token and space mode merely landed them on
  // a brand URL. Without this a reload re-mints them as a company user and "Return to
  // Internal" can no longer reach /internal (the guard bounces a non-internal user out).
  if (input.veraSpaceActive) {
    return { surface: 'internal', reason: 'an active veraSpace session (VERA-876)' }
  }

  if (isInternalPath(input.pathname)) {
    return { surface: 'internal', reason: `the internal route ${input.pathname}` }
  }

  if (isSurfaceNeutralPath(input.pathname)) {
    return {
      surface: defaultSurface,
      reason:
        `${input.pathname} names no surface, so VITE_DEV_DEFAULT_SURFACE ` +
        `(${defaultSurface}) decides`,
    }
  }

  // VRG-564: a named company surface is a company surface. The default does NOT reach here.
  return { surface: 'company', reason: `the company route ${input.pathname}` }
}

export const resolveDevSurface = (input: DevSurfaceInput): DevSurface =>
  describeDevSurface(input).surface

/**
 * `veraSpace` is a persisted zustand store; read the raw localStorage entry rather than the
 * store so this stays usable at module-evaluation time, before React (or the store's own
 * rehydration) has run.
 */
export const hasActiveVeraSpace = (): boolean => {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('veraSpace') : null
    return raw ? !!JSON.parse(raw)?.state?.companyId : false
  } catch {
    return false
  }
}

export const DEV_DEFAULT_SURFACE: DevSurface = readDefaultDevSurface(import.meta.env)

/**
 * Captured ONCE at module load (page load), BEFORE React Router can redirect — so a cold
 * load of `/internal/*` mints the internal account even though the guard bounces the URL
 * to a company page before the async mint completes. The URL you load decides the account;
 * switching surface means opening the other URL (or reloading).
 *
 * Every mint site reads THIS constant, so they cannot disagree.
 */
const initial =
  typeof window === 'undefined'
    ? { surface: DEV_DEFAULT_SURFACE, reason: 'no window (SSR/test)' }
    : describeDevSurface({
        pathname: window.location.pathname,
        veraSpaceActive: hasActiveVeraSpace(),
        defaultSurface: DEV_DEFAULT_SURFACE,
      })

export const INITIAL_DEV_SURFACE: DevSurface = initial.surface
export const INITIAL_DEV_SURFACE_REASON: string = initial.reason

/**
 * Say it out loud when the override is in play. VRG-564 was invisible precisely because
 * nothing announced which account the page had chosen or why — §17's enforcement step
 * ("check the bottom-left account chip") was the only signal, and it is manual.
 */
if (
  typeof window !== 'undefined' &&
  typeof console !== 'undefined' &&
  DEV_DEFAULT_SURFACE === 'internal'
) {
  console.warn(
    `[dev-session] VITE_DEV_DEFAULT_SURFACE=internal is set. This surface resolved to ` +
      `"${INITIAL_DEV_SURFACE}" because of ${INITIAL_DEV_SURFACE_REASON}. ` +
      `Named company routes ignore this override (working-conventions §17, VRG-564).`,
  )
}

// ---------------------------------------------------------------------------------------
// Minting
// ---------------------------------------------------------------------------------------

// Keyed BY SURFACE, not a single slot. Both call sites read `INITIAL_DEV_SURFACE`, so today
// they cannot disagree — but a single slot would mean that the day one of them passes a
// different surface, the second caller silently receives the FIRST caller's token for the
// wrong account. That is the exact failure this ticket is about, reintroduced one layer down.
// Keying costs a Map and removes the trap.
const inFlightMints = new Map<DevSurface, Promise<string | null>>()

/**
 * Mint a dev-session token, collapsing CONCURRENT callers onto one request.
 *
 * A cold load fires up to three mints — `checkAuth`, plus the 401 re-mint for any query
 * that raced ahead of the token landing. They all write the same cookie, so before this
 * they were a last-writer-wins race; that race is the mechanism by which any disagreement
 * between the (formerly duplicated) surface derivations became order-dependent. With one
 * derivation the three agreed on the account, but they still made three round-trips and
 * three cookie writes for one answer.
 *
 * Deduping only what is IN FLIGHT is deliberate: the promise is cleared when it settles,
 * so a genuine later re-mint (the dev token going stale mid-session, which is the whole
 * reason `reestablishDevSessionOnce` exists) still issues a fresh request.
 */
export const mintDevSession = (opts: {
  apiBaseUrl: string
  secret: string
  surface: DevSurface
}): Promise<string | null> => {
  const existing = inFlightMints.get(opts.surface)
  if (existing) return existing

  const request = axios
    .post<{ token?: string }>(`${opts.apiBaseUrl}/auth/dev/session`, {
      secret: opts.secret,
      surface: opts.surface,
    })
    .then((response) => response.data?.token ?? null)

  // `.finally` propagates the rejection, so a caller still sees a failed mint as a
  // rejection rather than a silent null.
  const tracked = request.finally(() => {
    inFlightMints.delete(opts.surface)
  })

  inFlightMints.set(opts.surface, tracked)
  return tracked
}

/** Test seam — drop any in-flight mint so specs do not leak state between cases. */
export const __resetDevSessionMint = () => {
  inFlightMints.clear()
}
